import type { Language } from "../types";
import type { Words } from "./copy";

/** How big the size they are picking really is: a drawing to scale, in
 *  millimetres, of a figure at that height beside an adult hand (about 18 cm
 *  from wrist to fingertip) and a €1 coin (23.25 mm across), with a ruler.
 *
 *  Ported from the website's SizeReality (MiniMeWonder/src/components/
 *  SizeReality.astro, docs/DECISIONS.md D22), which answers "how big is it,
 *  really?" on the product pages. Here it sits in the order sheet under the
 *  size chips, because "3 cm" and "8 cm" are abstract at the one moment the
 *  number is being chosen, and the dashed outlines show the sizes not picked.
 *  Labelled as an illustration, since a drawing is not a photo.
 *
 *  Pure SVG and one CSS variable: tapping a chip changes `--k` and the figure
 *  grows or shrinks on the baseline. Nothing is fetched for it. */
export default function SizeScale({
  sizes,
  picked,
  language,
  words: t,
}: {
  /** Every size offered, "3 cm"-style, the picked one among them. */
  sizes: string[];
  picked: string;
  /** For the decimal point in the inches: a comma in most of them. */
  language: Language;
  words: Words;
}) {
  const ks = sizes.map((size) => ({ size, k: kOf(size) })).filter((s): s is { size: string; k: number } => s.k !== null);
  const chosen = ks.find((s) => s.size === picked) ?? ks[Math.floor((ks.length - 1) / 2)];
  if (!ks.length || !chosen) return null;
  // The keychain sizes, 5 cm and under, are drawn close up: the coin, the
  // bottom of the ruler and the figure, which beside the whole hand would be
  // a speck. The figurines keep the hand.
  const close = Math.max(...ks.map((s) => s.k)) <= 0.5;

  return (
    <figure
      className={close ? "scale scale-close" : "scale"}
      style={{ "--k": chosen.k } as React.CSSProperties}
      aria-hidden="true"
    >
      <svg
        className="scale-svg"
        viewBox={close ? "134 148 150 74" : "0 0 284 222"}
        fill="none"
        stroke="currentColor"
        focusable="false"
      >
        <defs>
          <g id="scale-fig">
            <path d={FIG.body} />
            <circle cx={FIG.head.cx} cy={FIG.head.cy} r={FIG.head.r} />
            <rect x={FIG.base.x} y={FIG.base.y} width={FIG.base.w} height={FIG.base.h} rx="5" />
          </g>
        </defs>
        {/* The floor. */}
        <path d="M0 214.5H284" strokeWidth=".8" />
        {/* The hand: four fingers, a thumb, the palm and the wrist. */}
        <g strokeWidth="1.1" strokeLinejoin="round" className="scale-hand">
          <rect x="47" y="106" width="22" height="70" rx="11" transform="rotate(-35 58 172)" />
          <rect x="53" y="16" width="18.5" height="86" rx="9.25" />
          <rect x="72.5" y="8" width="19" height="94" rx="9.5" />
          <rect x="92.5" y="14" width="18.5" height="88" rx="9.25" />
          <rect x="112" y="30" width="17" height="72" rx="8.5" />
          <path d="M62 214V180H120V214" />
          <rect x="52" y="88" width="80" height="100" rx="18" />
          <path d="M60 154q18 10 38 6M106 114c-6 12-6 26-2 38" fill="none" strokeWidth=".7" opacity=".6" />
        </g>
        {/* The sizes not picked, as dashed outlines on the same baseline. */}
        <g strokeWidth=".8" strokeDasharray="2 2" className="scale-ghosts">
          {ks.map((s) => (
            <use
              key={s.size}
              href="#scale-fig"
              transform={`translate(${178 * (1 - s.k)} ${214 * (1 - s.k)}) scale(${s.k})`}
            />
          ))}
        </g>
        {/* The one picked, filled, scaled from its feet. */}
        <g className="scale-fig" stroke="none">
          <path d={FIG.body} className="scale-clay" />
          <circle cx={FIG.head.cx} cy={FIG.head.cy} r={FIG.head.r} className="scale-clay" />
          <rect x={FIG.base.x} y={FIG.base.y} width={FIG.base.w} height={FIG.base.h} rx="5" className="scale-ink" />
          <ellipse cx="178" cy="200.5" rx="22" ry="3" className="scale-shadow" />
        </g>
        {/* Its height, as a dimension line that scales with it. */}
        <path className="scale-dim" d="M214 114V214M209 114h10" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        {/* A €1 coin, 23.25 mm across, on the floor. */}
        <circle cx="245" cy="202.375" r="11.625" fill="#e7c66e" stroke="#7a5f56" strokeWidth=".9" />
        <circle cx="245" cy="202.375" r="7.6" stroke="#7a5f56" strokeWidth=".5" opacity=".7" />
        {/* The ruler: a tick every 5 mm, longer every centimetre. */}
        <path d={`M274 214V14${TICKS}`} strokeWidth=".6" />
        <g stroke="none" textAnchor="end" className="scale-ruler-text">
          <text x="264" y="166.5">5</text>
          <text x="264" y="116.5">10</text>
          <text x="264" y="66.5">15</text>
          <text x="264" y="16.5">20 cm</text>
        </g>
      </svg>
      <figcaption className="scale-key tiny muted">
        <span>{t.t("scaleTall", { size: chosen.size, inches: inchesOf(chosen.k, language) })}</span>
        <span>{t.t(close ? "scaleKeyClose" : "scaleKey")}</span>
      </figcaption>
    </figure>
  );
}

/** The figure, 100 mm tall on the baseline (y = 214), centred on x = 178. */
const FIG = {
  body: "M158 201C156 178 157 160 166 152Q178 144 190 152C199 160 200 178 198 201Z",
  head: { cx: 178, cy: 130, r: 16 },
  base: { x: 153, y: 200, w: 50, h: 14 },
};

/** A tick every 5 mm up the ruler, longer every 10, longest every 50. */
const TICKS = Array.from({ length: 41 }, (_, i) => {
  const y = 214 - i * 5;
  const len = i % 10 === 0 ? 7 : i % 2 === 0 ? 5 : 3;
  return `M274 ${y}h-${len}`;
}).join("");

/** The scale factor for a size: the figure is drawn at 10 cm, so "8 cm" is
 *  0.8. Null for a size not written in centimetres, which is then not drawn. */
function kOf(size: string): number | null {
  const cm = Number.parseFloat(size);
  if (!/\bcm$/.test(size.trim()) || !Number.isFinite(cm) || cm <= 0) return null;
  return Math.round((cm / 10) * 1000) / 1000;
}

/** "3.1", or "3,1" where the language writes it so, for a reader who thinks
 *  in inches. */
function inchesOf(k: number, language: Language): string {
  const inches = (k * 10) / 2.54;
  try {
    return new Intl.NumberFormat(language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(inches);
  } catch {
    return inches.toFixed(1);
  }
}
